import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useLang } from "../i18n";
import { Reveal } from "../components/Reveal";
import { HSlider } from "../components/HSlider";
import { Heart, Crown, ExternalLink } from "lucide-react";

export default function Donazione() {
  const { t } = useLang();
  const [wall, setWall] = useState(null);
  const [paypalUrl, setPaypalUrl] = useState("");

  useEffect(() => {
    api.get("/donations/wall").then(({ data }) => setWall(data)).catch(() => {});
    api.get("/settings").then(({ data }) => setPaypalUrl(data.paypal_url || "")).catch(() => {});
  }, []);

  const medals = ["text-wardogs", "text-[#C0C0C0]", "text-[#CD7F32]"];

  return (
    <main className="pt-32 pb-28">
      <div className="max-w-5xl mx-auto px-5 md:px-8">
        <Reveal>
          <p className="hud-label mb-3 text-wardogs">{t("don.over")}</p>
          <h1 className="font-display text-6xl md:text-8xl uppercase leading-[0.9] mb-6" data-testid="donazione-title">
            {t("don.title")}
          </h1>
          <p className="text-steel text-base md:text-lg max-w-xl mb-4">{t("don.text")}</p>
          <p className="text-steel text-sm max-w-xl mb-10">{t("don.note")}</p>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="max-w-2xl mb-16">
            <div className="chamfer border border-[#1A1A1A] bg-[#0C0C0C] overflow-hidden flex flex-col" data-testid="donazione-paypal-card">
              <img src="/donazioni/dona_paypal.png" alt="Dona con PayPal — WARDOGS.HQ" className="w-full block" data-testid="donazione-paypal-img" />
              <div className="p-7 md:p-8 flex-1 flex flex-col">
                <p className="text-sm text-steel leading-relaxed mb-7">{t("don.paypal_text")}</p>
                <div className="mt-auto">
                  <a
                    href={paypalUrl || "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="chamfer-sm bg-wardogs hover:bg-wardogs-hover text-[#050505] font-mono text-[11px] uppercase tracking-[0.2em] font-bold px-6 py-3.5 transition-colors duration-300 inline-flex items-center gap-2"
                    data-testid="donazione-paypal-cta"
                  >
                    {t("don.cta")} <ExternalLink className="w-4 h-4" />
                  </a>
                  <p className="font-mono text-[10px] text-[#4A4D51] mt-4">
                    {t("don.pool_note")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="mb-6 flex items-end justify-between">
            <div>
              <p className="hud-label mb-2 text-wardogs">// MURO DEI RINGRAZIAMENTI</p>
              <h2 className="font-display text-4xl uppercase leading-none" data-testid="donazione-wall-title">Top donatori</h2>
            </div>
            {wall && wall.total_count > 0 && (
              <span className="font-mono text-[10px] text-steel" data-testid="donazione-total">{wall.total_count} donazioni totali</span>
            )}
          </div>
          {!wall ? (
            <div className="chamfer border border-[#1A1A1A] bg-[#0C0C0C] p-6 animate-pulse h-32" />
          ) : wall.top.length === 0 ? (
            <div className="chamfer border border-[#1A1A1A] bg-[#0C0C0C] p-10 text-center" data-testid="donazione-wall-empty">
              <Heart className="w-8 h-8 text-[#2A2A2A] mx-auto mb-4" />
              <p className="font-mono text-xs text-steel">Nessuna donazione ancora. Il primo nome su questo muro potrebbe essere il tuo.</p>
            </div>
          ) : (
            <div className="grid lg:grid-cols-[380px_1fr] gap-6 items-start">
              <div className="flex flex-col gap-2" data-testid="donazione-wall">
                {wall.top.map((d, i) => (
                  <div
                    key={d.name}
                    className={`chamfer-sm border px-5 py-4 flex items-center gap-4 ${i === 0 ? "border-wardogs/60 bg-wardogs/10 donor-glow" : "border-[#1A1A1A] bg-[#0C0C0C]"}`}
                    data-testid={`donor-top-${i}`}
                  >
                    <span className={`font-display text-3xl w-10 shrink-0 ${medals[i] || "text-[#2A2A2A]"}`}>{String(i + 1).padStart(2, "0")}</span>
                    {i === 0 && <Crown className="w-5 h-5 text-wardogs shrink-0" />}
                    <span className="font-mono text-sm text-white font-bold truncate">{d.name}</span>
                    <span className="ml-auto font-mono text-sm text-wardogs font-bold shrink-0">{d.total} €</span>
                    <span className="font-mono text-[10px] text-[#4A4D51] shrink-0 hidden sm:inline">{d.count > 1 ? `${d.count} donazioni` : "1 donazione"}</span>
                  </div>
                ))}
              </div>
              <div className="chamfer border border-[#1A1A1A] bg-[#0C0C0C] p-6 min-w-0" data-testid="donazione-others-box">
                <p className="hud-label mb-5 text-steel">{t("don.wall_others")}</p>
                {wall.others.length === 0 ? (
                  <p className="font-mono text-[11px] text-[#4A4D51] leading-relaxed" data-testid="donazione-others-empty">{t("don.wall_others_empty")}</p>
                ) : (
                  <HSlider testid="donazione-others">
                    {wall.others.map((d, i) => (
                      <div key={d.name} className="snap-start shrink-0 w-52 chamfer-sm border border-[#1A1A1A] bg-[#050505] px-4 py-3" data-testid={`donor-other-${i}`}>
                        <div className="flex items-center gap-2 mb-1.5">
                          <Heart className="w-3.5 h-3.5 text-[#FFD700] shrink-0" fill="#FFD700" />
                          <span className="font-mono text-sm text-white font-bold truncate">{d.name}</span>
                        </div>
                        <p className="font-mono text-xs text-wardogs font-bold">{d.total} €</p>
                        <p className="font-mono text-[10px] text-[#4A4D51]">{d.count > 1 ? `${d.count} donazioni` : "1 donazione"}</p>
                      </div>
                    ))}
                  </HSlider>
                )}
              </div>
            </div>
          )}
        </Reveal>
      </div>
    </main>
  );
}
